import { CSS_URL_PATTERN, resolveUrl } from '~/lib/services/export/assetsProcessor'

interface CacheStore {
  sheets: CSSStyleSheet[]
  lengths: number[]
  // whether a content change replaces the CSSStyleSheet object (and thereby
  // fails the identity check) is engine-dependent - compare the inline
  // <style> texts as well, it is cheap next to re-serializing all cssRules
  styleTexts: (string | null)[]
  styleElement: HTMLStyleElement
}

let cache: CacheStore | null = null

export function absolutizeCssUrls(cssText: string, baseUrl: string = document.location.href) {
  return cssText.replace(CSS_URL_PATTERN, (_match, url: string) => `url("${resolveUrl(url, baseUrl)}")`)
}

/**
 * Extracts CSS Rules from the current page with caching.
 *
 * Use `force` to disable cache
 */
export async function extractCSSRules(force?: boolean): Promise<HTMLStyleElement> {
  const currentSheets = Array.from(document.styleSheets)

  // Safe-read lengths (handling CORS exceptions)
  const currentLengths = currentSheets.map((sheet) => {
    try {
      return sheet.cssRules.length
    } catch {
      return -1
    }
  })

  const currentStyleTexts = Array.from(document.querySelectorAll('style')).map((el) => el.textContent)

  const cacheHit =
    cache &&
    cache.sheets.length === currentSheets.length &&
    cache.sheets.every((sheet, i) => sheet === currentSheets[i]) &&
    cache.lengths.every((len, i) => len === currentLengths[i]) &&
    cache.styleTexts.length === currentStyleTexts.length &&
    cache.styleTexts.every((text, i) => text === currentStyleTexts[i])
  if (cacheHit && !force) {
    return cache!.styleElement.cloneNode(true) as HTMLStyleElement
  }

  // Cache miss: rebuild
  let css = ''
  for (const sheet of currentSheets) {
    try {
      const baseUrl = sheet.href || document.location.href
      for (const rule of sheet.cssRules) {
        let ruleText = rule.cssText
        // CRUCIAL: Translate Unicode-Symbols back to CSS-Escapes (\Fxxxx)
        if (ruleText.includes('mdi-')) {
          ruleText = escapeAllPrivateUnicodeRanges(ruleText)
        }

        css += absolutizeCssUrls(ruleText, baseUrl) + '\n'
      }
    } catch {
      /* empty */
    }
  }

  css = removeSourceMappingURL(css)

  const style = document.createElement('style')
  style.textContent = css

  // Store for next time
  cache = { sheets: currentSheets, lengths: currentLengths, styleTexts: currentStyleTexts, styleElement: style }

  return style.cloneNode(true) as HTMLStyleElement
}

/**
 * Escapes all characters from the three official Unicode Private Use Areas (PUA).
 * Covers all common icon fonts (MDI, FontAwesome, etc.).
 */
function escapeAllPrivateUnicodeRanges(text: string): string {
  return text.replace(/[\u{E000}-\u{F8FF}\u{F0000}-\u{FFFFF}\u{100000}-\u{10FFFF}]/gu, (match) => {
    const hex = match.codePointAt(0)!.toString(16).toUpperCase()
    return `\\${hex}`
  })
}

/**
 * @param {string} text
 * @returns {string}
 */
export function removeSourceMappingURL(text: string) {
  // Regulärer Ausdruck, der alles zwischen "/*# sourceMappingURL" und "*/" matcht
  return text.replace(/\/\*# sourceMappingURL.*?\*\//g, '')
}
